'use client'

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Tooltip. Figma: 10 / Components / Tooltip (TooltipContent).
 * Colors/surface/overlay with the default border, 12px padding, Roboto / text-sm.
 * The top-centre arrow places the tooltip below its trigger by default.
 */
function TooltipProvider({ delay = 200, ...props }: TooltipPrimitive.Provider.Props) {
    return (
        <TooltipPrimitive.Provider
            data-slot='tooltip-provider'
            delay={delay}
            {...props}
        />
    )
}

const Tooltip = TooltipPrimitive.Root

function TooltipTrigger({ className, ...props }: Omit<TooltipPrimitive.Trigger.Props, 'className'> & { className?: string }) {
    return (
        <TooltipPrimitive.Trigger
            data-slot='tooltip-trigger'
            className={className}
            {...props}
        />
    )
}

type TooltipContentProps = Omit<TooltipPrimitive.Popup.Props, 'className'> & {
    className?: string
    side?: TooltipPrimitive.Positioner.Props['side']
    align?: TooltipPrimitive.Positioner.Props['align']
    sideOffset?: number
}

function TooltipContent({ className, side = 'bottom', align = 'center', sideOffset = 8, children, ...props }: TooltipContentProps) {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Positioner side={side} align={align} sideOffset={sideOffset} className='z-50'>
                <TooltipPrimitive.Popup
                    data-slot='tooltip-content'
                    className={cn(
                        'max-w-76 origin-(--transform-origin) rounded-md border border-border-default bg-surface-overlay px-3 py-2.5 text-sm text-text-primary shadow-popover data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0',
                        className
                    )}
                    {...props}
                >
                    {children}
                    <TooltipPrimitive.Arrow className='data-[side=bottom]:-top-2 data-[side=left]:-right-3 data-[side=left]:rotate-90 data-[side=right]:-left-3 data-[side=right]:-rotate-90 data-[side=top]:-bottom-2 data-[side=top]:rotate-180'>
                        <svg width='16' height='8' viewBox='0 0 16 8' aria-hidden='true'>
                            <path d='M0 8L8 0L16 8' className='fill-surface-overlay' />
                            <path d='M0 8L8 0L16 8' fill='none' className='stroke-border-default' />
                        </svg>
                    </TooltipPrimitive.Arrow>
                </TooltipPrimitive.Popup>
            </TooltipPrimitive.Positioner>
        </TooltipPrimitive.Portal>
    )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
